import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Expense } from '../../models/expense.model';
import { modalHide, modalShow } from '../../utils/modal.util';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { foreignCurrencyDefault, foreignCurrencyFromRecord, foreignCurrencyPayload, isForeignCurrencyValid, ForeignCurrencyState } from '../element/InputForeignCurrency';
import { formatContact } from '../../scripts/format.value.script';
import { GlobalSwal } from '../../styles/swal.style';
import { legendInvalid } from '../../utils/legend.data.util';
import { suffixLegend } from '../../scripts/name.legend.script';

import AppPurchaseFormCreate from '../../interfaces/main/purchase/component/PurchaseFormCreate';
import AppPurchaseFormRequirements from '../../interfaces/main/purchase/component/PurchaseFormRequirements';
import AppModalAttachDocument from './ModalAttachDocument';

export const MODAL_EXPENSE_REPORT_EXPENSE_ID = 'modal-expense-report-expense'
const MODAL_ATTACH_DOC_EXPENSE_ID = 'modal-attach-document-expense-report-expense'

export interface ExpenseReportExpenseData {
  provider: { value: string; valid: boolean; object: any };
  concerning: { value: string; valid: boolean };
  date: { value: string; valid: boolean };
  currency: { value: string; valid: boolean; object: any };
  foreignCurrency: ForeignCurrencyState;
  requirements: {
    list: boolean;
    value: { type: string; description: string; contents: { detail: string; reference: string; quantity: string; price_unit: string; tax_included: boolean; tax_code: string; tax_rate: number; unit: string }[] }[];
    valid: { type: boolean; description: boolean; content: boolean; contents: { detail: boolean; quantity: boolean; price_unit: boolean; tax: boolean; unit: boolean }[] }[];
  };
  voucherFiles: { name: string; file: File; id_document_inbox?: string }[];
}

export function expenseToModalData(expense: Expense): {
  provider: { value: string; valid: boolean; object: any };
  concerning: string;
  date: string;
  currency: { value: string; valid: boolean; object: any };
  foreignCurrency: ForeignCurrencyState;
  requirements: { type: string; description: string; contents: any[] }[];
} {
  const provider = expense.provider
  const providerState = provider?.data?.id
    ? { value: provider.data.id, valid: true, object: provider }
    : { value: '', valid: false, object: null }

  const currencyObj = expense.foreign_currency
    ? { value: expense.foreign_currency.code, valid: true, object: { code: expense.foreign_currency.code, name: expense.foreign_currency.name ?? expense.foreign_currency.code, symbol: expense.foreign_currency.symbol ?? expense.foreign_currency.code } }
    : expense.currency
    ? { value: expense.currency.code, valid: true, object: expense.currency }
    : { value: '', valid: false, object: null }

  const fc = foreignCurrencyFromRecord(expense as any)

  const reqs = (expense.requirements ?? []).map(r => ({
    type: r.type ?? 'service',
    description: r.description ?? '',
    contents: (r.contents ?? []).map(c => ({
      detail: c.detail ?? '',
      reference: c.reference ?? '',
      quantity: c.quantity?.toString() ?? '1',
      price_unit: c.price_unit?.toString() ?? '',
      tax_included: c.tax_included ?? false,
      tax_code: c.tax_code ?? '',
      tax_rate: c.tax_rate ?? 0,
      unit: c.unit ?? 'unit'
    }))
  }))

  return {
    provider: providerState,
    concerning: expense.concerning ?? '',
    date: expense.created_date ?? '',
    currency: currencyObj,
    foreignCurrency: fc,
    requirements: reqs.length > 0 ? reqs : [{ type: 'service', description: '', contents: [{ detail: '', reference: '', quantity: '1', price_unit: '', tax_included: false, tax_code: '', tax_rate: 0, unit: 'unit' }] }]
  }
}

export interface ModalExpenseReportExpenseProps {
  companyForUser: any;
  editingExpense: Expense | null;
  defaultConcerning?: string;
  prefillVoucherFile?: File | null;
  prefillDocumentId?: string | null;
  prefillDocumentName?: string | null;
  onSave: (data: ExpenseReportExpenseData) => void;
  onCancel: () => void;
}

const ModalExpenseReportExpense: React.FunctionComponent<ModalExpenseReportExpenseProps> = ({
  companyForUser, editingExpense, defaultConcerning, prefillVoucherFile, prefillDocumentId, prefillDocumentName,
  onSave, onCancel
}) => {
  const { lang } = StateLanguage()

  const dateToday = format(new Date(), 'yyyy-MM-dd')
  const initialConcerning = defaultConcerning || ''

  const [provider, setProvider] = useState({ value: '', valid: false, object: null as any })
  const [concerning, setConcerning] = useState({ value: initialConcerning, valid: !!initialConcerning })
  const [date, setDate] = useState({ value: dateToday, valid: true })
  const [currency, setCurrency] = useState({ value: '', valid: false, object: null as any })
  const [foreignCurrency, setForeignCurrency] = useState<ForeignCurrencyState>(foreignCurrencyDefault)
  const [requirements, setRequirements] = useState<{
    list: boolean;
    value: { type: string; description: string; contents: { detail: string; reference: string; quantity: string; price_unit: string; tax_included: boolean; tax_code: string; tax_rate: number; unit: string }[] }[];
    valid: { type: boolean; description: boolean; content: boolean; contents: { detail: boolean; quantity: boolean; price_unit: boolean; tax: boolean; unit: boolean }[] }[];
  }>({
    list: true,
    value: [{ type: 'service', description: '', contents: [{ detail: '', reference: '', quantity: '1', price_unit: '', tax_included: false, tax_code: '', tax_rate: 0, unit: 'unit' }] }],
    valid: [{ type: true, description: true, content: true, contents: [{ detail: true, quantity: true, price_unit: true, tax: false, unit: true }] }]
  })

  const [voucherFile, setVoucherFile] = useState<File | null>(null)
  const [voucherDocId, setVoucherDocId] = useState<string | null>(null)
  const [voucherDocName, setVoucherDocName] = useState<string | null>(null)
  const [voucherView, setVoucherView] = useState<string>('')

  // Reset defaults for creation mode
  useEffect(() => {
    if (editingExpense) return
    const cVal = defaultConcerning || ''
    setConcerning({ value: cVal, valid: !!cVal })
    setDate({ value: dateToday, valid: true })
  }, [editingExpense, defaultConcerning]) // eslint-disable-line react-hooks/exhaustive-deps

  // Populate from editing expense
  useEffect(() => {
    if (!editingExpense) return

    const data = expenseToModalData(editingExpense)
    setProvider(data.provider)
    setConcerning({ value: data.concerning, valid: !!data.concerning })
    setDate({ value: data.date, valid: !!data.date })
    setCurrency(data.currency)
    setForeignCurrency(data.foreignCurrency)

    const reqValues = data.requirements
    const reqValids = reqValues.map(r => ({
      type: true,
      description: true,
      content: true,
      contents: r.contents.map(c => ({
        detail: !!c.detail,
        quantity: true,
        price_unit: !!c.price_unit,
        tax: !!c.tax_code,
        unit: true
      }))
    }))
    setRequirements({ list: true, value: reqValues, valid: reqValids })

    // Existing vouchers are already on the expense; we don't pre-fill the file input for edit
    setVoucherFile(null)
    setVoucherDocId(null)
    setVoucherDocName(null)
    setVoucherView('')
  }, [editingExpense])

  // Populate from prefilled PDF file
  useEffect(() => {
    if (!prefillVoucherFile) return
    setVoucherFile(prefillVoucherFile)
    setVoucherDocId(null)
    setVoucherDocName(null)
    setVoucherView(URL.createObjectURL(prefillVoucherFile))
  }, [prefillVoucherFile])

  // Populate from prefilled existing document
  useEffect(() => {
    if (!prefillDocumentId) return
    setVoucherFile(null)
    setVoucherDocId(prefillDocumentId)
    setVoucherDocName(prefillDocumentName ?? null)
    setVoucherView('')
  }, [prefillDocumentId, prefillDocumentName])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setVoucherFile(file)
    setVoucherDocId(null)
    setVoucherDocName(null)
    setVoucherView(URL.createObjectURL(file))
  }

  const handleRemoveVoucher = () => {
    setVoucherFile(null)
    setVoucherDocId(null)
    setVoucherDocName(null)
    setVoucherView('')
  }

  const handlePickExistingDocument = (id_document_inbox: string, doc?: { name: string }) => {
    setVoucherDocId(id_document_inbox)
    setVoucherDocName(doc?.name ?? null)
    setVoucherFile(null)
    setVoucherView('')
  }

  const handleSave = () => {
    const requirementsValid = requirements.valid.every((item) => isValidObjectAttributes(item))
    const hasRequirements = requirements.list || requirements.value.length > 0
    if (!concerning.valid || !date.valid || !currency.valid || !hasRequirements || !requirementsValid || !isForeignCurrencyValid(foreignCurrency)) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!concerning.valid) legendInvalid('legend-concerning-required')
        if (!date.valid) legendInvalid('legend-date-required')
        if (!currency.valid) legendInvalid('legend-currency-required')
        if (!isForeignCurrencyValid(foreignCurrency)) legendInvalid('legend-foreign-currency-required')
        if (!hasRequirements) legendInvalid('legend-requirements-required')
        if (!requirementsValid) {
          for (let i = 0; i < requirements.value.length; i++) {
            if (requirements.value[i].contents.length === 0) {
              legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
            } else {
              for (let j = 0; j < requirements.value[i].contents.length; j++) {
                if (!requirements.value[i].contents[j].detail) legendInvalid(`legend-requirement-content-detail-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].unit) legendInvalid(`legend-requirement-content-unit-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].quantity) legendInvalid(`legend-requirement-content-quantity-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].price_unit) legendInvalid(`legend-requirement-content-price-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].tax_rate) legendInvalid(`legend-requirement-content-tax-required${suffixLegend(i, j)}`)
              }
            }
          }
        }

        // Scroll to the first invalid field inside the modal
        const modalEl = document.getElementById(MODAL_EXPENSE_REPORT_EXPENSE_ID)
        const firstInvalid = modalEl?.querySelector('.d-block[id^="legend-"]') as HTMLElement | null
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      return
    }

    const voucherFiles: { name: string; file: File; id_document_inbox?: string }[] = []
    if (voucherFile instanceof File) {
      voucherFiles.push({ name: voucherFile.name, file: voucherFile })
    } else if (voucherDocId) {
      voucherFiles.push({ name: voucherDocName ?? 'document', file: null as any, id_document_inbox: voucherDocId })
    }

    onSave({
      provider,
      concerning,
      date,
      currency,
      foreignCurrency,
      requirements,
      voucherFiles
    })
    modalHide(MODAL_EXPENSE_REPORT_EXPENSE_ID)
  }

  const handleCancel = () => {
    onCancel()
    modalHide(MODAL_EXPENSE_REPORT_EXPENSE_ID)
  }

  if (!companyForUser) return null

  return (
    <>
    <div id={MODAL_EXPENSE_REPORT_EXPENSE_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{editingExpense ? lang.labels.editExpense : lang.labels.addExpense}</h2>
            <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={handleCancel}>
              <span className="svg-icon svg-icon-2x">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"/>
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body">
            <AppPurchaseFormCreate
              purchase="expense"
              provider={provider}
              concerning={concerning}
              date={date}
              currency={currency}
              foreignCurrency={foreignCurrency}
              title={editingExpense ? (editingExpense.expense_number || undefined) : undefined}
              description={editingExpense ? lang.labels.pleaseModifyAddOrDeleteFieldsFromExpense : undefined}
              setProvider={setProvider}
              setConcerning={setConcerning}
              setDate={setDate}
              setCurrency={setCurrency}
              setForeignCurrency={setForeignCurrency}
            />

            <AppPurchaseFormRequirements
              date={date.value}
              currency={currency.object ?? companyForUser.currency}
              foreignCurrency={foreignCurrency}
              requirements={requirements}
              setRequirements={setRequirements}
            />

            {/* Voucher / receipt section */}
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h2>{lang.labels.receipt}</h2>
                </div>
              </div>
              <div className="card-body pt-0">
                {/* Existing vouchers (read-only, edit mode only) */}
                { editingExpense?.vouchers && editingExpense.vouchers.length > 0 &&
                  <div className="mb-4">
                    <div className="fw-bold fs-7 text-muted mb-2">{lang.labels.vouchers}</div>
                    { editingExpense.vouchers.map((v: any, i: number) => (
                      <div key={i} className="d-flex align-items-center py-2 border-bottom">
                        <span className="svg-icon svg-icon-5 me-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                          </svg>
                        </span>
                        <span className="fw-bold fs-7">{v.original_name ?? v.name ?? `Document ${i + 1}`}</span>
                      </div>
                    ))}
                  </div>
                }

                { voucherFile || voucherDocId
                  ?
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge badge-light-primary fs-8 text-truncate mw-300px">
                      {voucherFile?.name ?? voucherDocName ?? lang.labels.receipt}
                    </span>
                    { voucherView &&
                      <a href={voucherView} target="_blank" rel="noopener noreferrer" className="btn btn-icon btn-sm btn-light-primary">
                        <span className="svg-icon svg-icon-5">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                          </svg>
                        </span>
                      </a>
                    }
                    <button className="btn btn-icon btn-sm btn-light-danger" type="button" onClick={handleRemoveVoucher}>
                      <span className="svg-icon svg-icon-5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                        </svg>
                      </span>
                    </button>
                  </div>
                  :
                  <div className="d-flex gap-2">
                    <label className="btn btn-sm btn-light-primary" htmlFor="modal-expense-receipt-input">
                      <span className="svg-icon svg-icon-5 me-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.attachReceipt}
                    </label>
                    <input
                      id="modal-expense-receipt-input"
                      className="d-none"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                    />
                    <button className="btn btn-sm btn-light-primary" type="button" onClick={() => modalShow(MODAL_ATTACH_DOC_EXPENSE_ID)}>
                      <span className="svg-icon svg-icon-5 me-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.attachExistingDocument}
                    </button>
                  </div>
                }
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" type="button" onClick={handleCancel}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" onClick={handleSave}>{lang.labels.save}</button>
          </div>
        </div>
      </div>
    </div>
    <AppModalAttachDocument
      id_company={companyForUser.id}
      entity_type="expense"
      onAttached={() => {}}
      onPick={handlePickExistingDocument}
      modalId={MODAL_ATTACH_DOC_EXPENSE_ID}
    />
    </>
  )
}

export default ModalExpenseReportExpense;
